<template>
    <div :class="{info: true, open: open, mobile: mobile()}">
        <img src="@/assets/christmas/closeIcon.svg" class="closeIcon" @click="$emit('close')">

        <div v-if="! mobile()" class="menu">
            <div v-for="(item, index) in info"
                 :class="{menuItem: true, selected: index === active, separator: item.lastOfType}"
                 @click="activate(index)">
                {{ item.title }}
            </div>
        </div>
        <div id="infoContent" class="content clearfix">
            <InfoContent
                    ref="article-0"
                    :index="0"
                    :info="info"
                    :activeIndex="active">

                <p>
                    Wenn Jesus am Kreuz nicht wirklich gestorben, sondern nur ohnmächtig geworden wäre, könnte er
                    natürlich leicht vorgeben, dass er von den Toten auferstanden sei. Oder?<br>
                    Es gibt zahlreiche Belege dafür, dass das nicht der Fall gewesen sein kann:
                <ol>
                    <li><b>Gesundheitlicher Zustand: </b>In den Berichten über die Kreuzigung lesen wir, dass Jesus
                        gegeißelt wurde. Das geschah zu jener Zeit mit Peitschen aus Lederriemen, in die Metallkugeln
                        eingeflochten
                        waren. Dadurch entstanden so tiefe Fleischwunden, dass die Haut der ausgepeitschten Personen
                        zerfetzt wurde und viel Blut verlorenging. Folglich litt Jesus laut dem Mediziner Dr. Alexander
                        Metherell
                        unter einem sogenannten hypovolämischen Schock, als er beim Tragen des Kreuzes kollabierte. Der
                        fatale gesundheitliche Zustand stellte die schlechteste Voraussetzung dar, um die Kreuzigung zu
                        überleben – <b>selbst, wenn er lebendig vom Kreuz genommen worden wäre</b>.
                    </li>
                    <li><b>Kreuzigung: </b>Bei einer Kreuzigung starb man unter unheimlichen Schmerzen durch Ersticken,
                        da das Atmen in der hängenden Position verunmöglicht wird. Solange man noch genug Kraft
                        hatte, konnte man sich mit den Füßen nach oben stemmen, was nach einigen Stunden wegen
                        Erschöpfung unmöglich wurde. Die verantwortlichen römischen Soldaten waren äußerst geübt darin,
                        zu erkennen, wenn jemand starb und aufhörte zu atmen – nicht zuletzt, weil es <b>die Todesstrafe
                            für sie bedeutet hätte</b>, einen Gefangenen lebend entkommen zu lassen. Sie hatten also ein
                        persönliches Interesse daran, keinen Fehler zu machen.
                    </li>
                    <li><b>Der Speer: </b>In den Berichten ist aufgezeichnet, dass ein Soldat Jesus noch einen Speer
                        in die Seite stieß, um sicherzugehen, dass er wirklich tot war. Dabei traten Blut und eine
                        wasserähnliche Flüssigkeit aus, wobei es sich laut Dr. Metherell um einen Perikarderguss
                        handelte – Flüssigkeit, die sich beim Herzversagen in den Membranen in der Nähe des Herzens
                        sammelte und beim Durchstechen mit dem Speer austrat. Das bedeutet nach modernen medizinischen
                        Erkenntnissen den sicheren Tod.
                    </li>
                </ol>
                Nachdem die Römer also sichergestellt hatten, dass Jesus wirklich tot war, wurde er in Steingrab
                gelegt, einbalsamiert und in Tücher gewickelt. Vor das Grab wurde ein großer Stein gewälzt –
                Berechnungen ergeben, dass dieser bei einem Durchmesser von etwa 1,50m etwa 1.500-2.000kg wog.<br>
                Vor diesen wurde eine schwer bewaffnete römische Wacheinheit aufgestellt. Selbst wenn Jesus also
                lebendig ins
                Grab gelegt worden wäre, hätte er es unmöglich lebend verlassen können.<br>
                Selbst unter der Annahme, dass dies dennoch möglich gewesen wäre, hätte er als "medizinisches Wrack",
                das eine intensivmedizinische Behandlung benötigt hätte,
                niemals gleich darauf von Ort zu Ort ziehen und den Menschen glaubhaft machen können, dass er von den
                Toten auferstanden und der Herr des Lebens sei.
                </p>
            </InfoContent>
            <InfoContent
                    ref="article-1"
                    :index="1"
                    :info="info"
                    :activeIndex="active">
                Wenn man sich ernsthaft mit der Frage beschäftigen will, ob Jesus von den Toten auferstanden ist, ist es
                wichtig, vorgefertigte Meinungen wie "es gibt keine Auferstehung aus den Toten" aus dem Spiel zu lassen
                –
                schließlich ist es ja genau das, was man herausfinden möchte. Wenn Jesus tatsächlich tot war und nach
                drei Tagen wieder lebte, bedeutet das, dass er von den Toten auferstanden sein musste. Das wollen wir
                nun
                anhand der Fakten überprüfen.
                <br>
                <ol>
                    <li><b>Das Grab war leer:</b> Der wohl stärkste Beweis dafür, dass das Grab von Jesus am dritten Tag
                        tatsächlich leer war, ist, dass es jeder ganz einfach überprüfen konnte. Hätten die Jünger
                        fälschlicherweise verkündet, dass es leer sei, wären sie sofort öffentlich von den Juden
                        widerlegt worden.
                    </li>
                    <li><b>Der Leichnam wurde nicht gestohlen: </b>Genau wegen jener Angst, dass die Jünger den toten
                        Körper von Jesus stehlen und dann verkündigen könnten, dass er auferstanden sei, wurde das Grab
                        mit einem römischen Siegel versiegelt und von einer Wacheinheit gesichert. Diese umfasste meist
                        16
                        gut ausgebildete und bewaffnete Soldaten, die abwechselnd schliefen und mit <b>ihrem Leben
                            bezahlen hätten müssen</b>, wenn sie ihre Aufgabe vernachlässigt hätten.<br>
                        Daher wäre es für die elf einfachen Jünger weder heimlich, noch gewaltsam möglich gewesen, den
                        Leichnam zu stehlen. Zudem hätten sie dafür nicht einmal ein Motiv gehabt, weil ein
                        aufgeflogener
                        Diebstahl niemanden glauben hätte lassen, dass Jesus von den Toten auferstanden sei.
                    </li>
                    <li><b>Jesus begegnete Leuten: </b>Im Neuen Testament lesen wir von vielen Personen, denen Jesus
                        nach seiner Auferstehung begegnet ist. Auch diese waren anfangs sehr skeptisch, weil eine
                        Auferstehung nun mal nicht üblich ist.<br>
                        Paulus, der zunächst selbst ein Verfolger der ersten
                        Christen war und nach einer Begegnung mit Jesus die Seite gewechselt hatte, schreibt
                        beispielsweise etwa 23 Jahre nach dem Tod von Jesus in einem Brief an die Gemeinde in Korinth,
                        dass Jesus 500 Personen gleichzeitig erschienen ist, wovon die meisten noch
                        lebten. Er forderte die Leser somit auf, die Augenzeugen selbst zu befragen. Hätte er gelogen,
                        wäre das sehr schnell aufgeflogen, weil eine solche Falschaussage sehr leicht zu widerlegen
                        gewesen wäre.<br>
                        Wir finden in den ersten Jahrzehnten nach dem Tod von Jesus tausende Christen, die die
                        Geschehnisse live miterlebt hatten und so überzeugt davon waren, dass ihr Herr lebte, dass sie
                        bereit waren, an ihrer Meinung festzuhalten, selbst wenn sie unter Verfolgung als Märtyrer
                        sterben mussten. [1]
                    </li>

                    <br>
                    [1] <a href="https://de.wikipedia.org/wiki/Christenverfolgungen_im_Römischen_Reich#Urchristentum"
                           target="_blank">Christenverfolgung im Urchristentum - Wikipedia</a>
                </ol>

            </InfoContent>
            <InfoContent
                    ref="article-2"
                    :index="2"
                    :info="info"
                    :activeIndex="active">
                <p>
                    Bei der Untersuchung und Bewertung eines vergangenen (historischen) Ereignisses ist es zunächst
                    wichtig zu erfahren, wie viele <b>glaubwürdige Augenzeugen</b> das Ereignis belegen können. Geht es
                    dabei
                    um eine Totenauferstehung, ist es umso essentieller zu wissen, ob die Zeugen leichtgläubig oder
                    wahnsinnig gewesen sein könnten, um so etwas zu glauben.
                    <br>
                    Wenn das Ereignis bereits vor längerer Zeit geschehen ist, ist der wichtigste Beleg für zuverlässige
                    textliche Überlieferungen, dass <b>Augenzeugen noch gelebt haben, um den Text zu kritisieren bzw. zu
                    bestätigen</b>. Wenn ich einen Verkehrsunfall beobachtet habe, aber im Polizeibericht falsche
                    Tatsachen
                    darüber angegeben werden, bin ich als Augenzeuge der wichtigste Kritiker, um diese Wahrheit zu
                    überprüfen. Je mehr Augenzeugen es gibt, desto sicherer ist, dass auch der Bericht korrigiert wurde.
                    In seinem Brief an die Christen in Korinth erwähnt der Apostel Paulus, dass der auferstandene Jesus
                    Christus von 500 Menschen auf einmal gesehen wurde. (Dies widerlegt auch die Theorie der
                    Halluzination, da diese kaum bei 500 Menschen gleichzeitig vorkommen würde.) Weiters verweist Paulus
                    darauf, dass zur Abfassungszeit des Briefs (ca. 56 n.Chr., also ca. 23 Jahre nach der Auferstehung
                    Jesu) die <b>Mehrzahl dieser Augenzeugen noch gelebt haben und dass sie zu den Ereignissen befragt
                    werden können</b>.
                    <br>
                    Frauen, die in der Bibel als erste Zeugen der Auferstehung angeführt werden (Matthäusevangelium
                    28,1), galten damals nach den jüdischen Prinzipien der Beweisführung als unzuverlässige und nicht
                    rechtskräftige Zeugen. Hätte man also versuchen wollen eine schlecht belegte Geschichte einer
                    Totenauferstehung zu beweisen, wäre das von keinem ernst genommen worden.
                    <br>
                    Selbst die Apostel wollten zunächst diesen Zeuginnen nicht glauben, als sie von ihren Erlebnissen
                    und der Auferstehung berichteten. Auch sie hielten Jesus für tot. Erst als sie sich selbst
                    vergewissert hatten, begannen sie zu verstehen. (Lukasevangelium 24, 9-12, Matthäusevangelium
                    28,8-10) Sie waren <b>keine naiven leichtgläubigen Menschen</b>, sondern geerdete Männer, die wohl
                    wussten,
                    dass kein Toter wieder zurückkommt.
                    <br>
                    Dazu kommt, dass dieses Ereignis <b>von den jüdischen Führern totgeschwiegen wurde</b>. Wenn die Auferstehung so
                    lächerlich gewesen wäre, wie es auf uns heute wirken mag, hätte sich auch die jüdische Obrigkeit
                    darüber zu Wort gemeldet. Jesus war ihnen ein Dorn im Auge. Sie hätten sehr leicht mit dem Leichnam
                    beweisen können, dass die ganze Geschichte um die Auferstehung erfunden ist, wenn dieser noch im
                    Grab gewesen wäre.
                    <br>
                    Der Apostel Paulus, ein strenggläubiger Jude, war zunächst ein Verfolger der Christen und deren
                    Lehre. Er ließ sich einen Freibrief vom Hohepriester in Jerusalem ausstellen, um im Umland die
                    Christen gefangen nehmen zu dürfen. Jesus zeigte sich Paulus, um ihn davon zu überzeugen, dass er
                    auf der falschen Seite kämpfte. <b>Paulus wurde erst nach diesem Augenzeugenerlebnis von einem
                    Verfolger zu einem Verfechter des Christentums.</b> (Apostelgeschichte 9, 1-19; Brief an die Christen in
                    Korinth 15,9)
                    <br>
                    Bei der Bewertung der Augenzeugenberichte wird man zumindest feststellen, dass weder
                    Massenhalluzination, noch leichtgläubige Menschen und Gerüchte als plausible Gründe für den Glauben
                    an die Auferstehung Jesu angeführt werden können. <b>Die jüdische Führungsschicht hatte keine Beweise,
                    um diese rebellische Gruppe der Anhänger eines auferstandenen Jesus im Keim zu ersticken.</b> Sie
                    setzten auf Verfolgung und Gefangennahme, um die Christen an der Verbreitung der Botschaft eines
                    Auferstandenen Jesus zu hindern. Einer ihrer vehementesten Verfolger, Paulus, kapituliert, nachdem
                    er selbst zum Augenzeugen des auferstandenen Jesus wird. Dieses für alle Menschen damals und heute
                    völlig unglaubliche Ereignis einer Totenauferstehung hat dazu geführt, dass <b>Menschen sich für die
                    Verbreitung dieser Tatsache verfolgen ließen und Kopf und Kragen dafür riskiert haben</b>, um die damit
                    verbundene Hoffnungsbotschaft der Bibel weiterzuerzählen. War das alles Wahnsinn, oder ist am Ende
                    doch etwas dran?
                </p>
            </InfoContent>
            <!--<InfoContent
                    ref="article-3"
                    :index="3"
                    :info="info"
                    :activeIndex="active">
                Die Apostel
                [1] <a href="https://de.wikipedia.org/wiki/Christenverfolgungen_im_Römischen_Reich#Urchristentum"
                       target="_blank">Christenverfolgung im Urchristentum - Wikipedia</a>
            </InfoContent>-->
        </div>
    </div>
</template>

<script>
    import Vue from 'vue'
    import Component from 'vue-class-component'
    import BasicComponent from "@/components/BasicComponent.js";
    import {Prop} from "vue-property-decorator";
    import InfoContent from "@/components/easter/info/InfoContent";
    import VueScrollTo from "vue-scrollto";

    @Component({
        components: {InfoContent}
    })
    export default class Info extends BasicComponent {

        @Prop(Boolean) open;
        @Prop(Array) infoArray;

        data() {
            return {
                active: 0,
            };
        }

        mounted() {
            this.register();
        }

        activate(index) {
            this.$data.active = index;
            document.getElementById("infoContent").scrollTop = 0;

            this.trackEvent('Ostern - Zusatzinfos aufgerufen', this.info[index].title);
            this.trackPage("Ostern Zusatzinfos - " + this.info[index].title);
        }

        openArticles(articles) {
            if (articles == null || articles.length === 0) return;
            this.activate(articles[0]);
            document.getElementById("infoContent").classList.remove("makeScrollable");
            for (let key of Object.keys(this.$refs)) {
                if (!key.startsWith("article-")) continue;
                this.$refs[key].setActive(false);
            }
            for (let article of articles) {
                //if (article !== articles[articles.length - 1]) continue; // only open last one of section for now
                this.$refs["article-" + article].setActive(true);
            }
            this.$nextTick(() => {
                document.getElementById("infoContent").scrollTop = 0;
            });
        }

        get info() {
            return this.infoArray;
        }


    }
</script>
<style scoped lang="scss">
    @import "@/stylesheets/Christmas.scss";

    .info {
        height: 100%;
        width: 100%;
        position: fixed;
        left: 0;
        top: 0;
        transform: translateX(120%);
        transition: 1s ease-in-out;
        z-index: 2500;
        padding: 30px;
        box-sizing: border-box;

        background-color: white;

        font-size: 20px;

        &.open {

            transform: none;
        }

        $menuWidth: 300px;

        .menu {
            display: inline-block;
            height: 100%;
            width: $menuWidth;
            float: left;

            .menuItem {
                padding: 10px;
                color: black;
                text-align: left;

                &:hover {
                    cursor: pointer;
                }

                &.selected {
                    color: #414f26;
                    font-weight: bold;
                }

                &.separator {
                    &::after {
                        content: " ";
                        display: block;
                        margin-top: 20px;
                        width: calc(100% - 40px);
                        border-bottom: 1px solid #707070;
                    }
                }
            }
        }

        .content {
            display: inline-block;
            height: 100%;
            width: calc(100% - #{$menuWidth});
            float: right;
            padding-top: 10px;
            overflow-y: scroll !important;

        }

        .closeIcon {
            position: absolute;
            top: 15px;
            right: 15px;
            height: 15px;
            //padding: 10px;

            &:hover {
                cursor: pointer;
            }
        }

    }

    .info.mobile {
        width: 100%;
        left: 0;

        .content {
            width: 100%;
        }

    }

</style>

<style lang="scss">

    .makeScrollable {
        -webkit-overflow-scrolling: touch;
    }

</style>
